import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, Tooltip } from '@material-ui/core'
import FileCopyIcon from '@material-ui/icons/FileCopyOutlined'
import CheckIcon from '@material-ui/icons/Check'
import VisibilityIcon from '@material-ui/icons/Visibility'

const useStyles = makeStyles(theme => ({
  proposalChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    margin: '8px 0',
    padding: '6px 10px',
    backgroundColor: 'var(--color-primary-050, #eff6ff)',
    border: '1px solid var(--color-primary-200, #bfdbfe)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-primary-700, #1d4ed8)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: 'var(--color-primary-100)'
    }
  },
  block: {
    margin: '8px 0',
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-sm)',
    overflow: 'hidden',
    backgroundColor: 'var(--color-neutral-050)'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 8px',
    fontSize: '10px',
    fontWeight: 600,
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'white'
  },
  copyBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    padding: '2px 6px',
    fontSize: '10px',
    fontWeight: 600,
    color: 'var(--color-neutral-500)',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 'var(--border-radius-sm)',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)',
      color: 'var(--color-neutral-700)'
    }
  },
  pre: {
    margin: 0,
    padding: theme.spacing(1),
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: '11px',
    color: 'var(--color-neutral-800)',
    overflow: 'auto',
    backgroundColor: 'white'
  },
  code: {
    background: 'transparent',
    padding: 0
  }
}))

function ChatCodeBlock ({ code, language, proposal, onApplyProposal }) {
  const classes = useStyles()
  const [copied, setCopied] = useState(false)

  const handleApply = () => {
    if (!onApplyProposal) return
    onApplyProposal(proposal)
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }).catch(() => {})
  }

  if (proposal && onApplyProposal) {
    return (
      <span
        className={classes.proposalChip}
        onClick={handleApply}
        role='button'
        tabIndex={0}
      >
        <VisibilityIcon style={{ fontSize: 14 }} />
        <span>{proposal.label || 'Proposal — click to review'}</span>
      </span>
    )
  }

  return (
    <div className={classes.block}>
      <div className={classes.header}>
        <span>{language || 'text'}</span>
        <Tooltip title={copied ? 'Copied' : 'Copy'} arrow>
          <button type='button' className={classes.copyBtn} onClick={handleCopy}>
            {copied ? <CheckIcon style={{ fontSize: 12 }} /> : <FileCopyIcon style={{ fontSize: 12 }} />}
            <span>{copied ? 'Copied' : 'Copy'}</span>
          </button>
        </Tooltip>
      </div>
      <pre className={classes.pre}><code className={classes.code}>{code}</code></pre>
    </div>
  )
}

ChatCodeBlock.propTypes = {
  code: PropTypes.string.isRequired,
  language: PropTypes.string,
  proposal: PropTypes.object,
  onApplyProposal: PropTypes.func
}

export default ChatCodeBlock
